import { SortOrder } from 'types/interfaces/sort';
import { SEARCH_DEFAULT_VIEW, SearchView } from 'constants/search';
import { SORT_OPTIONS } from './sortOptions';
import { sanitizeCardName } from 'utils/inputSanitization';
import { formatHasLegality, formatLabel } from 'constants/formats';
import { CardFinish, FINISH_LABELS } from 'utils/finishes';
import { finishTokenId, normalizeFinishes, removeFinish, toFinishParam } from 'utils/finishFilter';

/**
 * Canonical filter state for the /search workbench.
 *
 * This is the single source of truth: the filter panel, the toolbar tokens, the
 * syntax chip, the API request and the URL are all derived from one of these.
 * Nothing else on the page holds filter state, which is what keeps the tokens and
 * the panel from ever disagreeing.
 *
 * Numeric fields are strings so that "unset" ('') stays distinguishable from a
 * real zero ('0') — power/toughness of 0 is a legitimate query.
 */
export interface SearchFilterState {
  name: string;
  /** Exact-name match. Set by CardLink and buildCardUrl's "see all printings" links. */
  exact: boolean;
  oracleText: string;
  typeLine: string;
  setName: string;
  /** Exact-set match. Set by ViewCardPage's "browse this set" links. */
  exactSetName: boolean;
  flavorText: string;
  artist: string;
  manaCost: string;
  colors: string[];
  colorsMode: ColorsMode;
  cmc: string;
  cmcComparison: CmcComparison;
  power: string;
  powerComparison: StatComparison;
  toughness: string;
  toughnessComparison: StatComparison;
  /** Point bounds (100 points = $1). Points, never dollars — see spec Decision 16. */
  pointsMin: string;
  pointsMax: string;
  /**
   * Finish checkboxes (none = all, several = OR). Rendered only where a surface
   * passes showFinish (the offer builder); never read from or written to the URL.
   */
  finishes: CardFinish[];
  /** Someone wants this printing, so you could send it. The send side is the primary activity. */
  availableToSend: boolean;
  /** Someone offers this printing for trade, so you could receive it. */
  availableToReceive: boolean;
  format: string;
  formatLegality: FormatLegality;
  lang: string;
  reserved: boolean;
  sort: string;
  direction: SortOrder;
  /** 1-based results page (Asana 1219353862024235); 1 is omitted from the URL. */
  page: number;
  /** Result shape; 'printings' is the default and omitted from the URL. */
  view: SearchView;
}

export type ColorsMode = 'including' | 'exactly' | 'at_most';
export type CmcComparison = 'eq' | 'gt' | 'lt';
export type StatComparison = 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
export type FormatLegality = 'legal' | 'not_legal';

const COLORS_MODES: readonly ColorsMode[] = ['including', 'exactly', 'at_most'];
const CMC_COMPARISONS: readonly CmcComparison[] = ['eq', 'gt', 'lt'];
const STAT_COMPARISONS: readonly StatComparison[] = ['eq', 'gt', 'gte', 'lt', 'lte'];
const FORMAT_LEGALITIES: readonly FormatLegality[] = ['legal', 'not_legal'];

/** WUBRGC. Colors are normalized to this order so equal filters produce equal cache keys. */
export const COLOR_ORDER: readonly string[] = ['W', 'U', 'B', 'R', 'G', 'C'];

/**
 * The workbench opens on the highest-value cards rather than an empty form.
 * Held here rather than in sortOptions.ts, whose DEFAULT_SORT_KEY ('name') is the
 * backend's default and is relied on by other consumers.
 */
export const SEARCH_DEFAULT_SORT = 'point_value';
export const SEARCH_DEFAULT_DIRECTION: SortOrder = 'desc';

export const DEFAULT_SEARCH_STATE: SearchFilterState = {
  name: '',
  exact: false,
  oracleText: '',
  typeLine: '',
  setName: '',
  exactSetName: false,
  flavorText: '',
  artist: '',
  manaCost: '',
  colors: [],
  colorsMode: 'including',
  cmc: '',
  cmcComparison: 'eq',
  power: '',
  powerComparison: 'eq',
  toughness: '',
  toughnessComparison: 'eq',
  pointsMin: '',
  pointsMax: '',
  finishes: [],
  // Both default OFF. A trade filter that silently removes a searched card with
  // no explanation is a worse first experience than a broad default;
  // discoverability comes from the toggles leading the panel instead.
  availableToSend: false,
  availableToReceive: false,
  format: '',
  formatLegality: 'legal',
  lang: '',
  reserved: false,
  sort: SEARCH_DEFAULT_SORT,
  direction: SEARCH_DEFAULT_DIRECTION,
  page: 1,
  view: SEARCH_DEFAULT_VIEW,
};

const oneOf = <T extends string>(
  value: string | null,
  allowed: readonly T[],
  fallback: T,
): T => (value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback);

/** Keep only digits (and a leading minus for power/toughness, which can be negative). */
const cleanNumeric = (value: string | null, allowNegative = false): string => {
  if (!value) return '';
  const cleaned = allowNegative ? value.replace(/[^\d-]/g, '') : value.replace(/\D/g, '');
  return cleaned === '-' ? '' : cleaned;
};

/** Sort to WUBRGC and drop anything unrecognized or duplicated. */
export const normalizeColors = (colors: string[]): string[] => {
  const seen = new Set(colors.map((c) => c.toUpperCase()));
  return COLOR_ORDER.filter((c) => seen.has(c));
};

/**
 * Hydrate state from a URL.
 *
 * Accepts the legacy param vocabulary that /search/results already emits and that
 * crawlers have indexed, so old links keep working. `format` is lowercased on read
 * because the backend allowlist is lowercase — a crawled ?format=Commander URL
 * self-heals instead of 400ing.
 */
/** A page param is a positive integer or it is page 1; the real clamp waits for the total. */
const parsePage = (value: string | null): number => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : 1;
};

export const parseSearchState = (params: URLSearchParams): SearchFilterState => {
  const get = (key: string) => params.get(key) ?? '';
  const rawSort = params.get('sort');
  const sort = rawSort && SORT_OPTIONS.some((o) => o.key === rawSort) ? rawSort : SEARCH_DEFAULT_SORT;

  return {
    name: get('name'),
    exact: params.get('exact') === 'true',
    oracleText: get('oracleText'),
    typeLine: get('typeLine'),
    setName: get('setName'),
    exactSetName: params.get('exactSetName') === 'true',
    flavorText: get('flavorText'),
    artist: get('artist'),
    manaCost: get('manaCost'),
    colors: normalizeColors(get('colors').split(',').filter(Boolean)),
    colorsMode: oneOf(params.get('colors_mode'), COLORS_MODES, 'including'),
    cmc: cleanNumeric(params.get('cmc')),
    cmcComparison: oneOf(params.get('cmc_comparison'), CMC_COMPARISONS, 'eq'),
    power: cleanNumeric(params.get('power'), true),
    powerComparison: oneOf(params.get('power_comparison'), STAT_COMPARISONS, 'eq'),
    toughness: cleanNumeric(params.get('toughness'), true),
    toughnessComparison: oneOf(params.get('toughness_comparison'), STAT_COMPARISONS, 'eq'),
    pointsMin: cleanNumeric(params.get('points_min')),
    pointsMax: cleanNumeric(params.get('points_max')),
    // Not a URL param: only the non-URL offer builder can set it.
    finishes: [],
    availableToSend: params.get('availableToSend') === 'true',
    availableToReceive: params.get('availableToReceive') === 'true',
    format: get('format').toLowerCase(),
    // A hand-typed ?format=classic_legacy&formatLegality=not_legal reads as legal.
    formatLegality: formatHasLegality(get('format'))
      ? oneOf(params.get('formatLegality'), FORMAT_LEGALITIES, 'legal')
      : 'legal',
    lang: get('lang'),
    reserved: params.get('reserved') === 'true',
    sort,
    direction: params.get('direction') === 'asc' ? 'asc' : 'desc',
    page: parsePage(params.get('page')),
    view: params.get('view') === 'cards' ? 'cards' : SEARCH_DEFAULT_VIEW,
  };
};

/**
 * Write state back to a URL, omitting anything at its default so a bare /search
 * stays a clean canonical. Emits the same param names parseSearchState reads.
 */
export const serializeSearchState = (state: SearchFilterState): URLSearchParams => {
  const params = new URLSearchParams();
  const setIf = (key: string, value: string) => {
    if (value) params.set(key, value);
  };

  setIf('name', state.name.trim());
  if (state.exact && state.name.trim()) params.set('exact', 'true');
  setIf('oracleText', state.oracleText.trim());
  setIf('typeLine', state.typeLine.trim());
  setIf('setName', state.setName.trim());
  if (state.exactSetName && state.setName.trim()) params.set('exactSetName', 'true');
  setIf('flavorText', state.flavorText.trim());
  setIf('artist', state.artist.trim());
  setIf('manaCost', state.manaCost.trim());

  if (state.colors.length > 0) {
    params.set('colors', normalizeColors(state.colors).join(','));
    if (state.colorsMode !== 'including') params.set('colors_mode', state.colorsMode);
  }

  // A lone '-' is a half-typed negative, not a value. Serializing it would put
  // something on the URL that parses back as empty, desyncing the field from
  // the URL until the next edit.
  const isComplete = (value: string) => value !== '' && value !== '-';

  if (isComplete(state.cmc)) {
    params.set('cmc', state.cmc);
    params.set('cmc_comparison', state.cmcComparison);
  }
  if (isComplete(state.power)) {
    params.set('power', state.power);
    params.set('power_comparison', state.powerComparison);
  }
  if (isComplete(state.toughness)) {
    params.set('toughness', state.toughness);
    params.set('toughness_comparison', state.toughnessComparison);
  }

  // A min of 0 is not a filter — but it WOULD exclude every card with no point
  // value, so it must never reach the wire.
  if (state.pointsMin !== '' && Number(state.pointsMin) > 0) params.set('points_min', state.pointsMin);
  if (state.pointsMax !== '') params.set('points_max', state.pointsMax);

  if (state.availableToSend) params.set('availableToSend', 'true');
  if (state.availableToReceive) params.set('availableToReceive', 'true');

  if (state.format) {
    params.set('format', state.format);
    if (formatHasLegality(state.format)) params.set('formatLegality', state.formatLegality);
  }
  setIf('lang', state.lang);
  if (state.reserved) params.set('reserved', 'true');

  if (state.sort !== SEARCH_DEFAULT_SORT) params.set('sort', state.sort);
  if (state.direction !== SEARCH_DEFAULT_DIRECTION) params.set('direction', state.direction);
  if (state.view !== SEARCH_DEFAULT_VIEW) params.set('view', state.view);
  if (state.page > 1) params.set('page', String(state.page));

  return params;
};

/**
 * Everything but the page: the identity of a search. Two states with the same
 * search key differ only in which page of the same result set they show.
 */
export const searchKeyWithoutPage = (state: SearchFilterState): string => {
  const params = serializeSearchState(state);
  params.delete('page');
  return params.toString();
};

/**
 * Every URL param this page owns.
 *
 * A commit clears exactly these before writing, so anything else already on the
 * URL — a utm_* tag, a partner param — survives instead of being dropped.
 * Must stay in step with serializeSearchState.
 */
export const OWNED_URL_PARAMS: readonly string[] = [
  'name',
  'exact',
  'oracleText',
  'typeLine',
  'setName',
  'exactSetName',
  'flavorText',
  'artist',
  'manaCost',
  'colors',
  'colors_mode',
  'cmc',
  'cmc_comparison',
  'power',
  'power_comparison',
  'toughness',
  'toughness_comparison',
  'points_min',
  'points_max',
  'availableToSend',
  'availableToReceive',
  'format',
  'formatLegality',
  'lang',
  'reserved',
  'sort',
  'direction',
  'view',
  'page',
];

/**
 * Filters the user actually chose.
 *
 * Every token counts, the trade filters included: they default off, so one being
 * on is a deliberate choice and a genuine facet — the same treatment `reserved`
 * gets. (While the trade scope was default-ON it had to be excluded here, or
 * every page would have looked faceted and card-name searches would have lost
 * their self-canonical.)
 */
const chosenTokenIds = (state: SearchFilterState): string[] =>
  deriveTokens(state).map((t) => t.id);

/** True when the user narrowed the default view at all. */
export const hasActiveFilters = (state: SearchFilterState): boolean =>
  chosenTokenIds(state).length > 0;

/**
 * The count on the collapsed rail's badge (Asana 1219748466575111): one per
 * filter CONTROL whose value differs from its default, derived from the same
 * tokens Reset clears so the two can never disagree. Colours count once however
 * many are chosen (the mode alone counts nothing), points min and max count once
 * together, the finish checkboxes count once as a group. The name box is
 * excluded: it is the main search bar as well as a panel field, and a plain
 * search must not light the badge.
 */
export const countActiveFilters = (state: SearchFilterState): number => {
  const finishIds = new Set(normalizeFinishes(state.finishes).map(finishTokenId));
  const controls = new Set(
    chosenTokenIds(state)
      .filter((id) => id !== 'name')
      .map((id) => (finishIds.has(id) ? 'finishes' : id)),
  );
  return controls.size;
};

/** True when `name` is the only filter — the one filtered state we keep indexable. */
export const isNameOnlySearch = (state: SearchFilterState): boolean => {
  const ids = chosenTokenIds(state);
  return ids.length === 1 && ids[0] === 'name';
};

export interface SearchRequestArgs {
  name?: string;
  exact?: boolean;
  oracleText?: string;
  typeLine?: string;
  setName?: string;
  exactSetName?: boolean;
  flavorText?: string;
  artist?: string;
  manaCost?: string;
  colors?: string[];
  colorsMode?: ColorsMode;
  cmc?: number;
  cmc_comparison?: CmcComparison;
  power?: string;
  power_comparison?: StatComparison;
  toughness?: string;
  toughness_comparison?: StatComparison;
  pointsMin?: number;
  pointsMax?: number;
  /** Comma list of nonfoil|foil|etched in canonical order; absent = all finishes. */
  finish?: string;
  availableToSend?: boolean;
  availableToReceive?: boolean;
  format?: string;
  formatLegality?: FormatLegality;
  lang?: string;
  reserved?: boolean;
  sort?: string;
  direction?: SortOrder;
  /** Paging (Asana 1219353862024235). Set by the Search page, not by toSearchRequest. */
  page?: number;
  pageSize?: number;
  limit?: number;
  view?: SearchView;
}

/**
 * Build the API request. This is the ONLY place the page constructs one, and it
 * assigns keys in a fixed order so the RTK cache key is stable for equal filters.
 *
 * `name` and the other free-text fields are sanitized here rather than at the input,
 * so what the user typed stays intact in the field and in the URL.
 */
export const toSearchRequest = (state: SearchFilterState): SearchRequestArgs => {
  const args: SearchRequestArgs = {};

  const name = sanitizeCardName(state.name).trim();
  if (name) {
    args.name = name;
    if (state.exact) args.exact = true;
  }
  const oracleText = sanitizeCardName(state.oracleText).trim();
  if (oracleText) args.oracleText = oracleText;
  const typeLine = sanitizeCardName(state.typeLine).trim();
  if (typeLine) args.typeLine = typeLine;
  const setName = sanitizeCardName(state.setName).trim();
  if (setName) {
    args.setName = setName;
    if (state.exactSetName) args.exactSetName = true;
  }
  const flavorText = sanitizeCardName(state.flavorText).trim();
  if (flavorText) args.flavorText = flavorText;
  const artist = sanitizeCardName(state.artist).trim();
  if (artist) args.artist = artist;
  const manaCost = sanitizeCardName(state.manaCost).trim();
  if (manaCost) args.manaCost = manaCost;

  if (state.colors.length > 0) {
    // Normalized so that two equal filter sets produce byte-identical request
    // args, and therefore one RTK cache entry and one network request.
    args.colors = normalizeColors(state.colors);
    if (state.colorsMode !== 'including') args.colorsMode = state.colorsMode;
  }

  // Skip a half-typed '-' rather than sending it as an operand.
  if (state.cmc !== '' && state.cmc !== '-') {
    args.cmc = Number(state.cmc);
    args.cmc_comparison = state.cmcComparison;
  }
  if (state.power !== '' && state.power !== '-') {
    args.power = state.power;
    args.power_comparison = state.powerComparison;
  }
  if (state.toughness !== '' && state.toughness !== '-') {
    args.toughness = state.toughness;
    args.toughness_comparison = state.toughnessComparison;
  }

  if (state.pointsMin !== '' && Number(state.pointsMin) > 0) args.pointsMin = Number(state.pointsMin);
  if (state.pointsMax !== '') args.pointsMax = Number(state.pointsMax);

  // Canonical order, so equal selections share one cache entry (as colors above).
  const finish = toFinishParam(state.finishes);
  if (finish) args.finish = finish;

  // Each is sent only when on, never as an explicit false, so a bare search
  // carries neither param and is unfiltered.
  if (state.availableToSend) args.availableToSend = true;
  if (state.availableToReceive) args.availableToReceive = true;

  if (state.format) {
    args.format = state.format;
    if (formatHasLegality(state.format)) args.formatLegality = state.formatLegality;
  }
  if (state.lang) args.lang = state.lang;
  if (state.reserved) args.reserved = true;

  args.sort = state.sort;
  args.direction = state.direction;

  return args;
};

export interface FilterToken {
  /** Stable identifier, one per logical filter (a value+comparison pair is one token). */
  id: string;
  /** Plain-language label, e.g. "name: counterspell" or "mv < 3". */
  label: string;
  /** Pure removal of just this filter. */
  clear: (state: SearchFilterState) => SearchFilterState;
}

const COMPARISON_SYMBOL: Record<string, string> = {
  eq: '=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
};

const COLOR_MODE_LABEL: Record<ColorsMode, string> = {
  including: 'colors',
  exactly: 'colors exactly',
  at_most: 'colors at most',
};

/**
 * Tokens are derived, never stored — that is what guarantees the toolbar and the
 * filter panel can never disagree.
 */
export const deriveTokens = (state: SearchFilterState): FilterToken[] => {
  const tokens: FilterToken[] = [];

  // First, mirroring the panel: the two trade filters lead the sidebar, and they
  // narrow the result set harder than any card attribute. Labels are the exact
  // toggle labels, lowercased — the word "trade" never stands alone for these.
  if (state.availableToSend) {
    tokens.push({
      id: 'availableToSend',
      label: 'available to send',
      clear: (s) => ({ ...s, availableToSend: false }),
    });
  }
  if (state.availableToReceive) {
    tokens.push({
      id: 'availableToReceive',
      label: 'available to receive',
      clear: (s) => ({ ...s, availableToReceive: false }),
    });
  }

  const text = (id: keyof SearchFilterState, label: string, value: string) => {
    if (!value.trim()) return;
    tokens.push({
      id,
      label: `${label}: ${value.trim()}`,
      clear: (s) => ({ ...s, [id]: '' }),
    });
  };

  if (state.name.trim()) {
    tokens.push({
      id: 'name',
      label: `name${state.exact ? ' is' : ''}: ${state.name.trim()}`,
      clear: (s) => ({ ...s, name: '', exact: false }),
    });
  }
  text('oracleText', 'text', state.oracleText);
  text('typeLine', 'type', state.typeLine);
  if (state.setName.trim()) {
    tokens.push({
      id: 'setName',
      label: `set${state.exactSetName ? ' is' : ''}: ${state.setName.trim()}`,
      clear: (s) => ({ ...s, setName: '', exactSetName: false }),
    });
  }
  text('flavorText', 'flavor', state.flavorText);
  text('artist', 'artist', state.artist);
  text('manaCost', 'mana', state.manaCost);

  if (state.colors.length > 0) {
    // Normalize here too: state is canonical by construction, but a token that
    // renders WUBRGC regardless keeps the label stable for any caller.
    tokens.push({
      id: 'colors',
      label: `${COLOR_MODE_LABEL[state.colorsMode]}: ${normalizeColors(state.colors).join('')}`,
      clear: (s) => ({ ...s, colors: [], colorsMode: 'including' }),
    });
  }

  // One chip per checked finish, each clearing only itself (Decision 7).
  for (const finish of normalizeFinishes(state.finishes)) {
    tokens.push({
      id: finishTokenId(finish),
      label: FINISH_LABELS[finish],
      clear: (s) => ({ ...s, finishes: removeFinish(s.finishes, finish) }),
    });
  }

  if (state.cmc !== '') {
    tokens.push({
      id: 'cmc',
      label: `mv ${COMPARISON_SYMBOL[state.cmcComparison]} ${state.cmc}`,
      clear: (s) => ({ ...s, cmc: '', cmcComparison: 'eq' }),
    });
  }
  if (state.power !== '') {
    tokens.push({
      id: 'power',
      label: `power ${COMPARISON_SYMBOL[state.powerComparison]} ${state.power}`,
      clear: (s) => ({ ...s, power: '', powerComparison: 'eq' }),
    });
  }
  if (state.toughness !== '') {
    tokens.push({
      id: 'toughness',
      label: `toughness ${COMPARISON_SYMBOL[state.toughnessComparison]} ${state.toughness}`,
      clear: (s) => ({ ...s, toughness: '', toughnessComparison: 'eq' }),
    });
  }

  const hasMin = state.pointsMin !== '' && Number(state.pointsMin) > 0;
  const hasMax = state.pointsMax !== '';
  if (hasMin || hasMax) {
    const label = hasMin && hasMax
      ? `${state.pointsMin} - ${state.pointsMax} pts`
      : hasMin
        ? `over ${state.pointsMin} pts`
        : `under ${state.pointsMax} pts`;
    tokens.push({
      id: 'points',
      label,
      clear: (s) => ({ ...s, pointsMin: '', pointsMax: '' }),
    });
  }

  if (state.format) {
    tokens.push({
      id: 'format',
      label: formatHasLegality(state.format)
        ? `${state.format}${state.formatLegality === 'not_legal' ? ' (not legal)' : ' legal'}`
        : formatLabel(state.format),
      clear: (s) => ({ ...s, format: '', formatLegality: 'legal' }),
    });
  }

  if (state.lang) {
    tokens.push({
      id: 'lang',
      label: `language: ${state.lang}`,
      clear: (s) => ({ ...s, lang: '' }),
    });
  }

  if (state.reserved) {
    tokens.push({
      id: 'reserved',
      label: 'reserved list',
      clear: (s) => ({ ...s, reserved: false }),
    });
  }

  return tokens;
};
